import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex } from '@mantine/core';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { IPerson, IPersonsListParams, Person, Segment } from '@feathr/blackbox';
import { DebouncedInput, Input, Table, toast } from '@feathr/components';
import { SelectionActionBar } from '@feathr/extender/components';
import type { TPersonStatus } from '@feathr/extender/components/PersonStatusSegmentedControl';
import PersonStatusSegmentedControl from '@feathr/extender/components/PersonStatusSegmentedControl';
import { mergePersonStatusPredicate } from '@feathr/extender/components/PersonStatusSegmentedControl/PersonStatusSegmentedControl.utils';
import { useTableSelection } from '@feathr/extender/hooks';
import { useStore, useUser } from '@feathr/extender/state';
import { ARCHIVING_PERSONS, cssVar, SELECTED_PERSONS, useList, useToggle } from '@feathr/hooks';
import type { IListParams } from '@feathr/rachis';

import DataConfigure from '../DataCustomize';
import { filterPersonColumns } from '../PersonColumns';
import RefreshController from '../RefreshController';
import ArchiveButton from './ArchiveButton';
import usePersonStatusCounts from './usePersonStatusCounts';

import * as styles from './PersonsTable.css';

export type TVariant = 'default' | 'known' | 'anonymous';

interface IProps {
  segment: Segment;
  variant?: TVariant;
  personStatus?: TPersonStatus;
  onPersonStatusChange?: (status: TPersonStatus) => void;
}

const defaultPersonColumnIds: Record<TVariant, string[]> = {
  default: [
    'name',
    'email',
    'date_last_seen',
    'reachable',
    'seen_count',
    'date_last_matched_activity',
    'geoip.country_code',
    'options',
  ],
  known: [
    'name',
    'email',
    'occupation',
    'companies',
    'phone',
    'date_last_seen',
    'seen_count',
    'date_last_matched_activity',
    'options',
  ],
  anonymous: [
    'name',
    'reachable',
    'date_created',
    'date_last_seen',
    'seen_count',
    'date_last_matched_activity',
    'geoip.country_code',
    'options',
  ],
};

function PersonsTable({
  segment,
  variant = 'default',
  personStatus,
  onPersonStatusChange,
}: Readonly<IProps>): JSX.Element {
  const { CustomFields, Persons } = useStore();
  const [columnIds, setColumnIds] = useState(defaultPersonColumnIds[variant]);
  const [keywords, setKeywords] = useState<string | undefined>(undefined);
  const [reset, setReset] = useState(false);
  const [timeout, setTimeout] = useState<number | undefined>();
  const [shouldAutoRefresh, toggleAutoRefresh] = useToggle(false);
  const user = useUser();
  const { t } = useTranslation();
  // Selection functionality for table actions
  const selection = useTableSelection(SELECTED_PERSONS);

  // Track people with archive operations in progress
  const archivingList = useList(ARCHIVING_PERSONS);

  // Track when selectAll archive operation is in progress
  const [isSelectAllArchiving, setIsSelectAllArchiving] = useState(false);

  // When variant changes, reset columnIds to the current variant's defaults.
  useEffect(() => {
    setColumnIds(defaultPersonColumnIds[variant]);
  }, [variant]);

  useEffect(() => {
    // Stop resetting as soon as we have reloaded.
    if (reset) {
      setReset(false);
    }
  }, [reset]);

  useEffect(() => {
    if (!shouldAutoRefresh) {
      return;
    }

    // TODO: Create reusable hook for polling
    function poll(): void {
      setReset(true);
      // 60000ms = 1min, that's how often we want to auto-refresh the table
      setTimeout(window.setTimeout(poll, 60000, setReset, setTimeout));
    }

    if (timeout === undefined) {
      try {
        poll();
      } catch (e) {
        toast(t('There was an error updating your people. Please refresh the page.'), {
          type: 'error',
        });
      }
    }

    return () => {
      if (timeout) {
        window.clearTimeout(timeout);
      }
    };
  }, [t, timeout, setTimeout, shouldAutoRefresh]);

  // Some users may have checked off columns to hide them, so we need to make sure these are always shown.
  const alwaysShownColumns = ['name', 'options'];

  useEffect(() => {
    if (!user.isPending) {
      const userColumns = user.getSetting('persons_column_ids');
      if (userColumns && userColumns.length > 0) {
        setColumnIds([...userColumns, ...alwaysShownColumns]);
      }
      setColumnIds(defaultPersonColumnIds[variant]);
    }
  }, [user, variant]);

  const { clearSelection } = selection;

  useEffect(() => {
    // Clear selection when personStatus changes
    if (personStatus) {
      clearSelection();
      setReset(true);
      // Clear disabled rows when switching between active/archived tabs
      archivingList.reset();
      setIsSelectAllArchiving(false);
    }
  }, [personStatus, clearSelection]);

  const fields = CustomFields.list({
    filters: {
      collection: 'Person',
    },
  });

  function handleDebouncedSearchChange(newValue?: string): void {
    setKeywords(newValue);
  }

  function handleRefresh(): void {
    setReset(true);
  }

  function onChangeSettings(newColumnIds: string[]): void {
    user.setSetting('persons_column_ids', newColumnIds);
    setColumnIds(newColumnIds);
    user.patchDirty();
  }

  const filterElements = [
    <DebouncedInput<string>
      defaultValue={keywords}
      key={'search'}
      onChange={handleDebouncedSearchChange}
    >
      {(liveValue, onChangeLiveValue): JSX.Element => (
        <Input
          className={styles.input}
          isClearable={true}
          onChange={onChangeLiveValue}
          placeholder={t('Search by name, email, title, or company...')}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          type={'text'}
          value={liveValue}
        />
      )}
    </DebouncedInput>,
    <>
      <DataConfigure columnIds={columnIds} mode={'persons'} onChange={onChangeSettings} />
      <RefreshController
        handleRefresh={handleRefresh}
        shouldRefresh={shouldAutoRefresh}
        toggleRefresh={toggleAutoRefresh}
      />
    </>,
  ];

  const segmentMode = segment.get('mode');
  const lookbackMode = segment.get('lookback_mode');
  const lookbackValue = segment.get('lookback_value');
  const parentSegment = segment.get('parent_segment');
  const normalizedKeywords = keywords?.trim();

  const segmentPredicates = toJS(segment.get('predicates', []));

  const {
    activeCount: fetchedActiveCount,
    archivedCount: fetchedArchivedCount,
    isLoading: isStatusCountLoading,
  } = usePersonStatusCounts({
    segment,
    predicates: segmentPredicates,
    keywords: normalizedKeywords,
    resetDependencies: reset,
  });

  // Use personStatus prop if provided, otherwise default to 'active' to exclude archived people
  const effectivePersonStatus = personStatus ?? 'active';
  const isArchivedView = effectivePersonStatus === 'archived';
  const paramPredicates = effectivePersonStatus
    ? mergePersonStatusPredicate(segmentPredicates, effectivePersonStatus, segmentMode)
    : segmentPredicates;

  // When we merge with person status, we need to use match_all mode to ensure
  // the segment predicates (with their own mode) are ANDed with is_archived
  const paramMode = effectivePersonStatus ? 'match_all' : segmentMode;

  const params = {
    keywords,
    predicates: paramPredicates,
    mode: paramMode,
    lookback_mode: lookbackMode,
    lookback_value: lookbackValue,
    exclude: ['breadcrumbs'],
    include_date_last_matched_activity: true,
  } as IListParams<IPerson>;

  if (parentSegment) {
    params['parent_segment'] = parentSegment;
  }
  const personsList = Persons.list(params as Partial<IPersonsListParams>, {
    url: Persons.url('page'),
    reset,
  });

  const isListLoading = personsList.isPending;
  const activeCount = fetchedActiveCount ?? segment.count;
  const archivedCount = fetchedArchivedCount ?? personsList.pagination.count;
  const totalCount = isArchivedView ? archivedCount : activeCount;

  useEffect(() => {
    if (isArchivedView) {
      if (!isListLoading && !isStatusCountLoading) {
        const archivedSelectionCount =
          typeof totalCount === 'number' && !Number.isNaN(totalCount) ? totalCount : 0;
        selection.setTotalCount(archivedSelectionCount);
      }

      return;
    }

    if (!isStatusCountLoading && typeof activeCount === 'number' && !Number.isNaN(activeCount)) {
      selection.setTotalCount(activeCount);
    }
  }, [activeCount, isArchivedView, isListLoading, isStatusCountLoading, selection, totalCount]);

  // Handle successful archive/restore action
  function handleArchiveSuccess(): void {
    setReset(true);
    // Note: Don't reset isSelectAllArchiving here since archive operations run as background jobs
    // Rows should remain disabled until page is reloaded
  }

  // Archive action for selection
  const archiveAction = selection.hasSelection ? (
    <ArchiveButton
      action={personStatus === 'archived' ? 'restore' : 'archive'}
      onSelectAllArchiveStart={(): void => setIsSelectAllArchiving(true)}
      onSuccess={handleArchiveSuccess}
      predicates={selection.isSelectAllMode ? segmentPredicates : paramPredicates}
      segment={segment}
      selection={selection}
    />
  ) : undefined;

  // Create getTrProps to disable rows for people being archived
  const getTrProps = useCallback(
    (_state: unknown, rowInfo?: { original?: Person }) => {
      if (rowInfo?.original) {
        const person = rowInfo.original;
        const isBeingArchived = archivingList.has(person.id);

        if (isBeingArchived || isSelectAllArchiving) {
          return {
            className: styles.disabledRow,
          };
        }
      }
      return {};
    },
    [archivingList, isSelectAllArchiving],
  );

  // Enhanced setSelected that prevents selecting disabled rows
  const enhancedSetSelected = useCallback(
    (newSelection: string[] | ((prev: string[]) => string[])) => {
      if (typeof newSelection === 'function') {
        selection.setSelected((prev) => {
          const newSel = newSelection(prev);
          return newSel.filter((id) => !archivingList.has(id));
        });
      } else {
        const filteredSelection = newSelection.filter((id) => !archivingList.has(id));
        selection.setSelected(filteredSelection);
      }
    },
    [selection, archivingList],
  );

  return (
    <>
      {personStatus && onPersonStatusChange && (
        <Flex mb={cssVar('--spacing-4')}>
          <PersonStatusSegmentedControl
            activeCount={personStatus === 'active' ? activeCount : undefined}
            archivedCount={isArchivedView ? archivedCount : undefined}
            isLoading={isListLoading || isStatusCountLoading}
            onChange={onPersonStatusChange}
            value={personStatus}
          />
        </Flex>
      )}
      {selection.hasSelection && (
        <SelectionActionBar
          actions={archiveAction}
          selectAllButtonText={
            selection.totalCount
              ? t('Select all {{count}} person', { count: selection.totalCount })
              : undefined
          }
          selectAllSelectedText={
            selection.totalCount
              ? t('All {{count}} person selected', { count: selection.totalCount })
              : undefined
          }
          selectedText={
            selection.isSelectAllMode
              ? t('All people selected')
              : t('{{count}} person selected', { count: selection.count })
          }
          selection={selection}
        />
      )}

      <Table<Person>
        collection={Persons}
        columns={filterPersonColumns(columnIds, fields.models, t)}
        filterElements={filterElements}
        filterLabel={t('Quick search')}
        getTrProps={getTrProps}
        initialSort={[{ id: 'date_last_seen', desc: true }]}
        isSelectable={true}
        isSelectAllMode={selection.isSelectAllMode}
        key={`persons-table-${effectivePersonStatus}`}
        listOptions={{ url: Persons.url('page'), reset }}
        onAutoSelectPage={selection.autoSelectPage}
        params={params}
        selected={selection.selected}
        setSelected={enhancedSetSelected}
        wrapperMode={'simple'}
      />
    </>
  );
}

export default observer(PersonsTable);
